import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BASE}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach token from localStorage as fallback (cookies are primary)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("rimmoget_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // DEV impersonation header — scopes all backend calls to the impersonated society
  const imp = localStorage.getItem("rimmoget_impersonate_id");
  if (imp) config.headers["X-Impersonate-Society"] = imp;
  return config;
});

// Detect 403 "Société fermée" → fire a global event so the AuthContext can refresh
// the user payload and the ReadOnlyBanner appears immediately.
// Detect 401 anywhere (except on the login endpoint itself) → the session has
// expired; clear the token and let the AuthContext redirect to /login.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const url = (error.config?.url || "");
    if (status === 401 && !url.includes("/auth/login") && !url.includes("/auth/logout")) {
      // Token expired or invalidated server-side. Purge local state and
      // notify the app so protected routes bounce back to /login.
      localStorage.removeItem("rimmoget_token");
      localStorage.removeItem("rimmoget_impersonate_id");
      localStorage.removeItem("rimmoget_impersonate_name");
      window.dispatchEvent(new Event("rimmoget:session-expired"));
    }
    if (status === 403 && typeof detail === "string" && /société fermée/i.test(detail)) {
      window.dispatchEvent(new Event("rimmoget:society-closed"));
    }
    return Promise.reject(error);
  },
);

export const impersonation = {
  start(id, name) {
    localStorage.setItem("rimmoget_impersonate_id", id);
    localStorage.setItem("rimmoget_impersonate_name", name || "");
    window.dispatchEvent(new Event("rimmoget:impersonation"));
  },
  stop() {
    localStorage.removeItem("rimmoget_impersonate_id");
    localStorage.removeItem("rimmoget_impersonate_name");
    window.dispatchEvent(new Event("rimmoget:impersonation"));
  },
  current() {
    return {
      id: localStorage.getItem("rimmoget_impersonate_id") || "",
      name: localStorage.getItem("rimmoget_impersonate_name") || "",
    };
  },
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function downloadPdf(url, fallbackName = "document.pdf") {
  return downloadBlob(url, fallbackName, "application/pdf");
}

export async function downloadBlob(url, fallbackName, mime = "application/octet-stream") {
  const res = await api.get(url, { responseType: "blob" });
  const cd = res.headers["content-disposition"] || "";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  const name = m ? m[1] : fallbackName;
  const blob = new Blob([res.data], { type: res.data?.type || mime });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
}
